/**
 * SUBSTANCE_RECALIBRATION gate (operator-signed 2026-08-05) — manager UI.
 *
 * Two operator tools for the editorial-substance 1→5 recalibration, wired on
 * the same async pattern as the other Tools-page cards: submit → run_id →
 * poll GET /tools/runs/{id} → structured result. Both are READ-ONLY. (These
 * backend tools report completion via the run-status endpoint, not the
 * 'assayerai:tools' channel, so the poll is authoritative.)
 *
 *  - Re-score preview: the pre-flip DIFF. Re-scores a random sample of
 *    article-producing announcements on the widened v2 scale (always v2,
 *    regardless of the assayer_substance_scale_v2_enabled flag) and shows the
 *    old→new distribution + transition matrix. Review this, then flip the flag.
 *  - Distribution check: the collapse guardrail — trailing-window distribution
 *    + breach verdict (a band dominating the scored pool, the 07-13 signature).
 */
import { useState, useEffect } from 'react';
import { Play, Loader2, CheckCircle, XCircle, AlertTriangle, Gauge, Scale } from 'lucide-react';
import {
  getToolRunStatus,
  runSubstanceRescorePreview,
  runSubstanceDistributionCheck,
} from '../lib/api/tools.js';

type RunState = 'idle' | 'submitting' | 'running' | 'done' | 'error';

const BANDS = [1, 2, 3, 4, 5] as const;
const BAND_LABEL: Record<number, string> = {
  1: 'procedural',
  2: 'light event',
  3: 'routine-material',
  4: 'notably material',
  5: 'exceptional',
};

interface RescoreResult {
  sample_requested: number;
  sample_scored: number;
  sample_failed: number;
  window_days: number;
  old_distribution: Record<string, number>;
  new_distribution: Record<string, number>;
  transition_matrix: Record<string, number>;
  examples: Record<string, Array<{ title: string; doc_form: string | null }>>;
}

interface DistributionResult {
  window_days: number;
  article_pool_only: boolean;
  threshold: number;
  total_scored: number;
  counts: Record<string, number>;
  shares: Record<string, number>;
  dominant_band: number | null;
  dominant_share: number;
  breach: boolean;
}

/**
 * Shared async-run hook: submit → run_id → poll GET /tools/runs/{id} → result.
 * The same submit/track/render pattern as the other Tools-page cards; these
 * backend tools report completion through the run-status endpoint (they do not
 * emit on the 'assayerai:tools' channel), so the poll is authoritative.
 */
function useToolRun<T>() {
  const [runState, setRunState] = useState<RunState>('idle');
  const [runId, setRunId] = useState<string | null>(null);
  const [result, setResult] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Poll — 60 attempts × 5s (5 min ceiling).
  useEffect(() => {
    if (runState !== 'submitting' && runState !== 'running') return;
    if (!runId) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        if (cancelled) return;
        try {
          const s = await getToolRunStatus(runId);
          if (s.status === 'completed') {
            setResult((s.result as T) || null);
            setRunState('done');
            return;
          }
          if (s.status === 'failed') {
            setError(s.error || 'Job failed');
            setRunState('error');
            return;
          }
        } catch { /* keep polling */ }
      }
    })();
    return () => { cancelled = true; };
  }, [runState, runId]);

  const submit = async (fn: () => Promise<{ run_id: string }>) => {
    setRunState('submitting');
    setError(null);
    setResult(null);
    setRunId(null);
    try {
      const sub = await fn();
      setRunId(sub.run_id);
      setRunState('running');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  return { runState, result, error, submit };
}

const pct = (n: number, total: number) => (total ? `${Math.round((n / total) * 1000) / 10}%` : '—');

function DistBar({ dist, highlightCollapse }: { dist: Record<string, number>; highlightCollapse?: boolean }) {
  const total = BANDS.reduce((s, b) => s + (dist[String(b)] || 0), 0);
  return (
    <div className="space-y-1">
      {BANDS.map((b) => {
        const n = dist[String(b)] || 0;
        const share = total ? n / total : 0;
        const collapsed = highlightCollapse && share > 0.7;
        return (
          <div key={b} className="flex items-center gap-2 text-xs">
            <span className="w-28 shrink-0 text-grey-600">{b} · {BAND_LABEL[b]}</span>
            <div className="flex-1 bg-grey-100 rounded h-4 overflow-hidden">
              <div
                className={`h-4 ${collapsed ? 'bg-amber-500' : 'bg-primary-500'}`}
                style={{ width: `${Math.round(share * 100)}%` }}
              />
            </div>
            <span className="w-20 shrink-0 text-right tabular-nums text-grey-700">
              {n} · {pct(n, total)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function SubstanceRescorePreviewCard() {
  const [sampleSize, setSampleSize] = useState(60);
  const [days, setDays] = useState(21);
  const { runState, result, error, submit } = useToolRun<RescoreResult>();
  const busy = runState === 'submitting' || runState === 'running';

  const r = result;
  const newTotal = r ? BANDS.reduce((s, b) => s + (r.new_distribution[String(b)] || 0), 0) : 0;
  const newTop = r && newTotal
    ? Math.max(...BANDS.map((b) => (r.new_distribution[String(b)] || 0))) / newTotal
    : 0;
  const spreadRestored = r ? newTop <= 0.7 : false;

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Scale className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Substance Re-score Preview (v2 1–5 gate)</h3>
            <p className="text-sm text-grey-600 mt-1">
              Re-scores a random sample of <strong>article-producing</strong> announcements on the widened
              <strong> 1–5</strong> scale — always v2, regardless of the rollout flag — and shows the old→new
              distribution + transition matrix. This is the diff to review <strong>before</strong> flipping
              <code className="text-xs bg-grey-100 px-1 rounded mx-1">assayer_substance_scale_v2_enabled</code>.
              Read-only.
            </p>
          </div>
        </div>
        <button
          onClick={() => submit(() => runSubstanceRescorePreview(sampleSize, days))}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {busy ? 'Scoring…' : 'Run preview'}
        </button>
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs">
        <label className="flex items-center gap-1.5 text-grey-700">
          Sample size
          <input
            type="number" min={5} max={400} value={sampleSize}
            onChange={(e) => setSampleSize(Math.max(5, Math.min(400, Number(e.target.value) || 60)))}
            disabled={busy}
            className="w-20 rounded border-grey-300 px-2 py-1"
          />
        </label>
        <label className="flex items-center gap-1.5 text-grey-700">
          Window (days)
          <input
            type="number" min={1} max={120} value={days}
            onChange={(e) => setDays(Math.max(1, Math.min(120, Number(e.target.value) || 21)))}
            disabled={busy}
            className="w-20 rounded border-grey-300 px-2 py-1"
          />
        </label>
      </div>

      {runState === 'running' && (
        <p className="mt-3 text-xs text-grey-500 flex items-center gap-1.5">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Scoring {sampleSize} announcements on Gemini — up to a minute…
        </p>
      )}
      {runState === 'error' && (
        <p className="mt-3 text-xs text-danger-700 flex items-center gap-1.5"><XCircle className="h-4 w-4" /> {error}</p>
      )}

      {runState === 'done' && r && (
        <div className="mt-4 space-y-4">
          <div className={`flex items-center gap-2 text-sm rounded-md px-3 py-2 ${spreadRestored ? 'bg-success-50 text-success-700' : 'bg-amber-50 text-amber-700'}`}>
            {spreadRestored ? <CheckCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {spreadRestored
              ? 'Spread restored — no band exceeds 70% under v2.'
              : `Still concentrated — the top band holds ${Math.round(newTop * 1000) / 10}% under v2.`}
            <span className="text-grey-500 ml-auto">
              {r.sample_scored} scored{r.sample_failed ? ` · ${r.sample_failed} failed` : ''} · {r.window_days}d
            </span>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-xs font-semibold text-grey-700 mb-1.5">OLD (current 1–3)</div>
              <DistBar dist={r.old_distribution} highlightCollapse />
            </div>
            <div>
              <div className="text-xs font-semibold text-grey-700 mb-1.5">NEW (v2 1–5)</div>
              <DistBar dist={r.new_distribution} highlightCollapse />
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-grey-700 mb-1.5">Transitions (old → new)</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(r.transition_matrix)
                .sort((a, b) => b[1] - a[1])
                .map(([k, n]) => (
                  <span key={k} className="text-xs bg-grey-50 border border-grey-200 rounded px-2 py-1 tabular-nums text-grey-700">
                    {k} · <strong>{n}</strong>
                  </span>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function SubstanceDistributionCheckCard() {
  const [days, setDays] = useState(14);
  const [articlePoolOnly, setArticlePoolOnly] = useState(true);
  const { runState, result, error, submit } = useToolRun<DistributionResult>();
  const busy = runState === 'submitting' || runState === 'running';
  const r = result;

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Gauge className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Substance Distribution Guardrail</h3>
            <p className="text-sm text-grey-600 mt-1">
              Current trailing-window <code className="text-xs bg-grey-100 px-1 rounded">editorial_substance</code>
              {' '}distribution + collapse verdict — a single band dominating the scored pool (the 07-13 signature).
              The check the drift lacked. Read-only.
            </p>
          </div>
        </div>
        <button
          onClick={() => submit(() => runSubstanceDistributionCheck(days, articlePoolOnly))}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {busy ? 'Checking…' : 'Check now'}
        </button>
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs">
        <label className="flex items-center gap-1.5 text-grey-700">
          Window (days)
          <input
            type="number" min={1} max={90} value={days}
            onChange={(e) => setDays(Math.max(1, Math.min(90, Number(e.target.value) || 14)))}
            disabled={busy}
            className="w-20 rounded border-grey-300 px-2 py-1"
          />
        </label>
        <label className="flex items-center gap-1.5 text-grey-700 cursor-pointer select-none">
          <input
            type="checkbox" checked={articlePoolOnly}
            onChange={(e) => setArticlePoolOnly(e.target.checked)}
            disabled={busy} className="rounded border-grey-300"
          />
          Article-producing pool only
        </label>
      </div>

      {runState === 'error' && (
        <p className="mt-3 text-xs text-danger-700 flex items-center gap-1.5"><XCircle className="h-4 w-4" /> {error}</p>
      )}

      {runState === 'done' && r && (
        <div className="mt-4 space-y-3">
          <div className={`flex items-center gap-2 text-sm rounded-md px-3 py-2 ${r.breach ? 'bg-danger-50 text-danger-700' : 'bg-success-50 text-success-700'}`}>
            {r.breach ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
            {r.breach
              ? `COLLAPSE: band ${r.dominant_band} holds ${Math.round(r.dominant_share * 1000) / 10}% (> ${Math.round(r.threshold * 100)}%).`
              : `Healthy — top band ${r.dominant_band ?? '—'} at ${Math.round(r.dominant_share * 1000) / 10}%.`}
            <span className="text-grey-500 ml-auto">{r.total_scored} scored · {r.window_days}d</span>
          </div>
          <DistBar dist={r.counts} highlightCollapse />
        </div>
      )}
    </div>
  );
}
